import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { alpha, useTheme } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import {
  createCourierControl,
  deleteCourierControl,
  getCourierControl,
  listCourierControl,
  unificarCourierControl,
  updateCourierControl,
} from "../../api/CourierControl/courierControlApi";
import { getUserById } from "../../api/User/userApi";
import { socket } from "../../socket";
import {
  canCourierAccess,
  canCourierSolicitar,
  canCourierTerminar,
} from "../../utils/courierPermissions";
import {
  canSelectCourierForMerge,
  findSiblingIdsByAwb,
  validateMergeCourierShipments,
} from "../../utils/courierMergeShipments";
import {
  isCourierLeftWithClientEnvio,
  isCourierLeftWithClientMotivo,
  courierMotivoI18nKey,
} from "../../utils/courierControlSelects";
import { classifyCourierPiezasKind } from "../../utils/courierControlPure";
import {
  guardLockedDialogClose,
  lockedDialogSlotProps,
} from "../../utils/lockedDialogUi";
import {
  pageContentCardSx,
  pageFieldSx,
  pageHeroShellSx,
  pageKpiChipSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import CourierControlDialog, {
  emptyEnvio,
  emptyLine,
} from "./CourierControlDialog";
import {
  healCourierLineMotivo,
  normalizeCourierMotivoValue,
} from "../../utils/courierSamplePiece";

const piezasKindChipSx = (theme, kind) => {
  if (kind === "swatches") {
    return {
      ...pageKpiChipSx,
      height: 26,
      minWidth: 36,
      fontWeight: 800,
      bgcolor: alpha("#f9a825", 0.18),
      color: "#f57f17",
      border: `1px solid ${alpha("#f9a825", 0.55)}`,
    };
  }
  if (kind === "mixed") {
    return {
      ...pageKpiChipSx,
      height: 26,
      minWidth: 36,
      fontWeight: 800,
      bgcolor: alpha("#ef6c00", 0.16),
      color: "#e65100",
      border: `1px solid ${alpha("#ef6c00", 0.5)}`,
    };
  }
  return {
    ...pageKpiChipSx,
    height: 26,
    minWidth: 36,
    fontWeight: 800,
    bgcolor: alpha(theme.palette.success.main, 0.14),
    color: theme.palette.success.dark,
    border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`,
  };
};

const mapEnvioLineas = (envio = {}) => {
  const lines = Array.isArray(envio.lineas) ? envio.lineas : [];
  if (!lines.length) return [emptyLine()];
  return lines.map((linea) => emptyLine(healCourierLineMotivo(linea, envio)));
};

const fmtNum = (value, digits = 2) => {
  if (value == null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtDate = (value) => {
  const raw = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
};

const fmtBoxMeasures = (row = {}) => {
  const L = Number(row.LargoCm);
  const W = Number(row.AnchoCm);
  const H = Number(row.AltoCm);
  if (![L, W, H].every((n) => Number.isFinite(n) && n > 0)) return "—";
  return `${fmtNum(L, 1)} × ${fmtNum(W, 1)} × ${fmtNum(H, 1)} cm`;
};

/** Detalle de pieza en el desglose expandido (Producto, Color, Técnica, Talla, Peso…). */
const buildContenidoDetailParts = (linea = {}, t, fmtNumFn) => {
  const parts = [];
  const push = (labelKey, value) => {
    const v = String(value || "").trim();
    if (!v) return;
    parts.push({
      key: labelKey,
      label: t(`courierControl.${labelKey}`),
      value: v,
    });
  };
  push("season", linea.Temporada);
  push("brand", linea.Marca);
  push("product", linea.TipoPrenda);
  push("sampleType", linea.TipoMuestra);
  push("color", linea.Color);
  push("technique", linea.Tejido);
  push("size", linea.Talla);
  if (linea.PesoKg != null && Number.isFinite(Number(linea.PesoKg))) {
    parts.push({
      key: "weight",
      label: t("courierControl.lineWeight"),
      value: `${fmtNumFn(linea.PesoKg, 3)} kg`,
    });
  }
  return parts;
};

const CONTENIDO_CHIP_COLORS = {
  season: "#0288d1",
  brand: "#5e35b1",
  product: "#00897b",
  sampleType: "#6a1b9a",
  color: "#e65100",
  technique: "#546e7a",
  size: "#c62828",
  weight: "#2e7d32",
};

const contenidoDetailChipSx = (theme, key) => {
  const color = CONTENIDO_CHIP_COLORS[key] || theme.palette.primary.main;
  return {
    height: 22,
    fontWeight: 700,
    fontSize: "0.68rem",
    maxWidth: "100%",
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.4)}`,
    "& .MuiChip-label": { px: 0.85 },
  };
};

const statusChipSx = (theme, estado) => {
  const map = {
    PEDIDO: theme.palette.info.main,
    PREPARANDO_ADUANAS: theme.palette.secondary?.main || "#7b1fa2",
    EN_CURSO: theme.palette.warning.main,
    ENTREGADO: theme.palette.success.main,
    CANCELADO: theme.palette.error.main,
  };
  const color = map[estado] || theme.palette.info.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 800,
    bgcolor: alpha(color, 0.14),
    color,
    border: `1px solid ${alpha(color, 0.45)}`,
  };
};

const directionChipSx = (theme, direccion) => {
  const color =
    direccion === "ENTRADA"
      ? theme.palette.secondary?.main || "#7b1fa2"
      : theme.palette.primary.main;
  return {
    ...pageKpiChipSx,
    height: 26,
    fontWeight: 700,
    bgcolor: alpha(color, 0.12),
    color,
    border: `1px solid ${alpha(color, 0.4)}`,
  };
};

const filterToggleSx = (theme) => ({
  borderRadius: "999px !important",
  border: `1px solid ${alpha(theme.palette.divider, 0.7)} !important`,
  overflow: "hidden",
  bgcolor: alpha(
    theme.palette.common.black,
    theme.palette.mode === "dark" ? 0.2 : 0.03,
  ),
  height: 34,
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.72rem",
    px: 1.25,
    py: 0,
    height: 34,
    border: "none !important",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      bgcolor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": {
        bgcolor: theme.palette.primary.dark,
      },
    },
  },
});

const tableHeadCellSx = (theme) => ({
  fontWeight: 800,
  fontSize: "0.68rem",
  letterSpacing: 0.35,
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  bgcolor: `${theme.palette.background.paper} !important`,
  backgroundColor: `${theme.palette.background.paper} !important`,
  backgroundImage: "none !important",
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
  whiteSpace: "nowrap",
  py: 1.25,
  zIndex: 3,
  top: 0,
  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.9)}`,
});

export default function CourierControl() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { contentMargin } = useContext(SidebarContext);
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [direccion, setDireccion] = useState("");
  const [ambito, setAmbito] = useState("");
  const [estado, setEstado] = useState("PEDIDO");
  const [q, setQ] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyEnvio());
  const [saving, setSaving] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permsLoaded, setPermsLoaded] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeInfo, setMergeInfo] = useState("");

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const decoded = token ? jwtDecode(token) : null;
        const userId = decoded?.userId || decoded?.id;
        if (!userId) {
          if (!cancelled) {
            setPermUser({ isAdmin: !!decoded?.isAdmin });
            setPermsLoaded(true);
          }
          return;
        }
        const user = await getUserById(userId);
        if (!cancelled) {
          setPermUser({
            ...(user || {}),
            isAdmin: user?.isAdmin ?? decoded?.isAdmin,
          });
          setPermsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPermUser({});
          setPermsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const allowAccess = canCourierAccess(permUser || {});
  const allowSolicitar = canCourierSolicitar(permUser || {});
  const allowTerminar = canCourierTerminar(permUser || {});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listCourierControl({
        direccion: direccion || undefined,
        ambito: ambito || undefined,
        estado: estado || undefined,
        q: q.trim() || undefined,
      });
      setRows(Array.isArray(data) ? data : []);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.loadError"));
    } finally {
      setLoading(false);
    }
  }, [direccion, ambito, estado, q, t]);

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return undefined;
    const timer = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, q, permsLoaded, allowAccess]);

  // Tiempo real China ↔ España
  useEffect(() => {
    if (!permsLoaded || !allowAccess) return undefined;
    const onCourier = () => {
      load();
    };
    socket.on("courier-control-updated", onCourier);
    return () => {
      socket.off("courier-control-updated", onCourier);
    };
  }, [permsLoaded, allowAccess, load]);

  // Si el diálogo está abierto, refrescar el draft cuando cambia ese envío
  useEffect(() => {
    if (!dialogOpen || !draft?.IdCourierControl) return undefined;
    const id = Number(draft.IdCourierControl);
    const onCourier = async (payload) => {
      if (Number(payload?.IdCourierControl) !== id) return;
      if (payload?.action === "delete") {
        setDialogOpen(false);
        return;
      }
      try {
        const envio = await getCourierControl(id);
        setDraft({
          ...emptyEnvio(),
          ...envio,
          Fecha: String(envio.Fecha || "").slice(0, 10),
          FechaEnvio: String(envio.FechaEnvio || "").slice(0, 10),
          FechaEntregaDestino: String(envio.FechaEntregaDestino || "").slice(
            0,
            10,
          ),
          lineas: mapEnvioLineas(envio),
        });
      } catch {
        /* ignore */
      }
    };
    socket.on("courier-control-updated", onCourier);
    return () => socket.off("courier-control-updated", onCourier);
  }, [dialogOpen, draft?.IdCourierControl]);

  const openDraft = (envio) => {
    setDraft({
      ...emptyEnvio(),
      ...envio,
      Fecha: String(envio.Fecha || "").slice(0, 10),
      FechaEnvio: String(envio.FechaEnvio || "").slice(0, 10),
      FechaEntregaDestino: String(envio.FechaEntregaDestino || "").slice(0, 10),
      lineas: mapEnvioLineas(envio),
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!permsLoaded || !allowAccess) return;
    const nueva = params.get("nueva");
    const id = params.get("id");
    const muestraId = params.get("muestraId");
    const codigo = params.get("codigo") || "";

    if (nueva === "1") {
      if (!allowSolicitar) {
        setParams({}, { replace: true });
        return;
      }
      openDraft(
        emptyEnvio({
          lineas: [
            emptyLine({
              IdMuestra: muestraId ? Number(muestraId) : null,
              CodigoMuestra: codigo,
            }),
          ],
        }),
      );
      setParams({}, { replace: true });
      return;
    }

    if (id) {
      getCourierControl(id)
        .then((envio) => openDraft(envio))
        .catch(() => setError(t("courierControl.loadError")));
      setParams({}, { replace: true });
    }
  }, [params, setParams, t, permsLoaded, allowAccess, allowSolicitar]);

  const save = async (payloadOverride = null) => {
    // onClick pasa el evento; solo aceptar override explícito de envío
    const isEnvioOverride =
      payloadOverride &&
      typeof payloadOverride === "object" &&
      !payloadOverride.nativeEvent &&
      (Object.prototype.hasOwnProperty.call(payloadOverride, "Estado") ||
        Object.prototype.hasOwnProperty.call(payloadOverride, "lineas") ||
        Object.prototype.hasOwnProperty.call(
          payloadOverride,
          "IdCourierControl",
        ) ||
        Object.prototype.hasOwnProperty.call(payloadOverride, "NumeroAwb"));
    const payload = isEnvioOverride ? { ...draft, ...payloadOverride } : draft;
    const prevEstado = String(draft.Estado || "PEDIDO").toUpperCase();
    const nextEstado = String(payload.Estado || "PEDIDO").toUpperCase();
    const confirmingShip =
      isEnvioOverride &&
      allowSolicitar &&
      !allowTerminar &&
      prevEstado === "PEDIDO" &&
      nextEstado === "EN_CURSO";
    const draftAlreadyLocked =
      !allowTerminar &&
      (prevEstado === "PREPARANDO_ADUANAS" ||
        prevEstado === "EN_CURSO" ||
        prevEstado === "ENTREGADO" ||
        prevEstado === "CANCELADO");
    const canCreate = allowSolicitar;
    const canUpdate =
      allowTerminar ||
      (allowSolicitar && (!draftAlreadyLocked || confirmingShip));
    if (payload.IdCourierControl) {
      if (!canUpdate) {
        setError(t("courierControl.saveError"));
        return;
      }
    } else if (!canCreate) {
      setError(t("courierControl.saveError"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (payload.IdCourierControl) {
        await updateCourierControl(payload.IdCourierControl, payload);
      } else {
        await createCourierControl(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!draft.IdCourierControl || !allowTerminar) return;
    if (!window.confirm(t("courierControl.deleteConfirm"))) return;
    setSaving(true);
    try {
      await deleteCourierControl(draft.IdCourierControl);
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const directionLabel = (value) =>
    value === "ENTRADA" ? t("courierControl.entry") : t("courierControl.exit");
  const scopeLabel = (value) =>
    value === "INTERNACIONAL"
      ? t("courierControl.international")
      : t("courierControl.national");
  const statusLabel = (value) => {
    if (value === "EN_CURSO") return t("courierControl.statusEnCurso");
    if (value === "PREPARANDO_ADUANAS")
      return t("courierControl.statusPreparandoAduanas");
    if (value === "ENTREGADO") return t("courierControl.statusEntregado");
    if (value === "CANCELADO") return t("courierControl.statusCancelado");
    return t("courierControl.statusPedido");
  };

  const kpis = useMemo(() => {
    const counts = {
      total: rows.length,
      PEDIDO: 0,
      PREPARANDO_ADUANAS: 0,
      EN_CURSO: 0,
      ENTREGADO: 0,
    };
    rows.forEach((row) => {
      if (counts[row.Estado] != null) counts[row.Estado] += 1;
    });
    return counts;
  }, [rows]);

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(row.IdCourierControl)),
    [rows, selectedIds],
  );

  const mergeValidation = useMemo(
    () => validateMergeCourierShipments(selectedRows),
    [selectedRows],
  );

  const sameAwbSiblingIds = useMemo(() => {
    if (selectedIds.size !== 1) return [];
    const [seedId] = selectedIds;
    return findSiblingIdsByAwb(rows, seedId);
  }, [rows, selectedIds]);

  const mergeableRows = useMemo(
    () => rows.filter((row) => canSelectCourierForMerge(row)),
    [rows],
  );

  const allMergeableSelected =
    mergeableRows.length > 0 &&
    mergeableRows.every((row) => selectedIds.has(row.IdCourierControl));

  const toggleSelectAllMergeable = () => {
    if (allMergeableSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(mergeableRows.map((row) => row.IdCourierControl)));
  };

  const selectSameAwbSiblings = () => {
    if (sameAwbSiblingIds.length < 2) return;
    setSelectedIds(new Set(sameAwbSiblingIds));
  };

  const openMergeConfirm = () => {
    if (!mergeValidation.ok) {
      setError(
        t(`courierControl.${mergeValidation.messageKey}`, {
          status: mergeValidation.status,
        }),
      );
      return;
    }
    setMergeInfo("");
    setMergeOpen(true);
  };

  const confirmMerge = async () => {
    if (!mergeValidation.ok) return;
    setMerging(true);
    setError("");
    try {
      const result = await unificarCourierControl({
        ids: selectedRows.map((row) => row.IdCourierControl),
        targetId: mergeValidation.targetId,
      });
      setMergeOpen(false);
      setSelectedIds(new Set());
      setMergeInfo(
        t("courierControl.mergeSuccess", {
          id: result?.IdCourierControl || mergeValidation.targetId,
        }),
      );
      await load();
      if (result?.envio) {
        openDraft(result.envio);
      }
    } catch (err) {
      setError(err?.response?.data?.error || t("courierControl.mergeError"));
    } finally {
      setMerging(false);
    }
  };

  const openRow = (id) => {
    getCourierControl(id)
      .then(openDraft)
      .catch(() => setError(t("courierControl.loadError")));
  };

  if (permsLoaded && !allowAccess) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {t("courierControl.noPermission")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...contentMargin,
        p: { xs: 1.5, md: 2.5 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        height: "100dvh",
        minHeight: "100dvh",
        maxHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 28%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 32%)`,
      }}
    >
      <Box sx={{ flexShrink: 0, mb: 1.5 }}>
        <Paper
          elevation={0}
          sx={{
            ...pageHeroShellSx(theme),
            mb: 0,
            backdropFilter: "none",
            overflow: "hidden",
            background:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper
                : "linear-gradient(135deg, #eef8f9 0%, #f7f4ff 52%, #f3faf6 100%)",
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, md: 2 },
              py: { xs: 1.1, md: 1.25 },
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                minWidth: 0,
                flex: { xs: "1 1 auto", md: "0 1 auto" },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.28)} 0%, ${alpha(theme.palette.secondary?.main || "#7b1fa2", 0.16)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.45)}`,
                }}
              >
                <LocalShippingOutlinedIcon
                  sx={{ color: "primary.main", fontSize: 22 }}
                />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{
                    letterSpacing: 1.2,
                    display: "block",
                    fontWeight: 800,
                    lineHeight: 1,
                    mb: 0.2,
                    fontSize: "0.65rem",
                  }}
                >
                  {t("courierControl.overline")}
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.1,
                    fontSize: { xs: "1.15rem", md: "1.35rem" },
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("courierControl.title")}
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.75}
              sx={{
                flex: { xs: "1 1 100%", md: "1 1 auto" },
                minWidth: 0,
                alignItems: "center",
              }}
            >
              <Chip
                size="small"
                label={t("courierControl.kpiTotal", { count: kpis.total })}
                sx={{
                  ...pageKpiChipSx,
                  height: 26,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  fontWeight: 800,
                }}
              />
              <Chip
                size="small"
                label={`${kpis.PEDIDO} ${t("courierControl.statusPedido")}`}
                sx={{ ...statusChipSx(theme, "PEDIDO"), height: 26 }}
              />
              {kpis.PREPARANDO_ADUANAS ? (
                <Chip
                  size="small"
                  label={`${kpis.PREPARANDO_ADUANAS} ${t("courierControl.statusPreparandoAduanas")}`}
                  sx={{
                    ...statusChipSx(theme, "PREPARANDO_ADUANAS"),
                    height: 26,
                  }}
                />
              ) : null}
              <Chip
                size="small"
                label={`${kpis.EN_CURSO} ${t("courierControl.statusEnCurso")}`}
                sx={{ ...statusChipSx(theme, "EN_CURSO"), height: 26 }}
              />
              <Chip
                size="small"
                label={`${kpis.ENTREGADO} ${t("courierControl.statusEntregado")}`}
                sx={{ ...statusChipSx(theme, "ENTREGADO"), height: 26 }}
              />
            </Stack>

            {allowSolicitar ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => openDraft(emptyEnvio())}
                sx={{
                  ...pagePrimaryButtonSx(theme),
                  ml: { md: selectedIds.size ? 0 : "auto" },
                  alignSelf: "center",
                  whiteSpace: "nowrap",
                  px: 1.75,
                  py: 0.85,
                  borderRadius: 999,
                  boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              >
                {t("courierControl.newRequest")}
              </Button>
            ) : null}

            {selectedIds.size > 0 ? (
              <Stack
                direction="row"
                flexWrap="wrap"
                alignItems="center"
                gap={1}
                sx={{
                  width: { xs: "100%", md: "auto" },
                  ml: { md: "auto" },
                }}
              >
                <Chip
                  size="small"
                  label={t("courierControl.mergeSelected", {
                    count: selectedIds.size,
                  })}
                  onDelete={() => setSelectedIds(new Set())}
                  sx={{
                    ...pageKpiChipSx,
                    height: 28,
                    fontWeight: 800,
                    bgcolor: alpha(
                      theme.palette.secondary?.main || "#7b1fa2",
                      0.12,
                    ),
                    color: theme.palette.secondary?.main || "#7b1fa2",
                  }}
                />
                {sameAwbSiblingIds.length > selectedIds.size ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectSameAwbSiblings}
                    sx={{
                      borderRadius: 999,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    {t("courierControl.mergeSelectSameAwb", {
                      count: sameAwbSiblingIds.length,
                    })}
                  </Button>
                ) : null}
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<CallMergeIcon />}
                  disabled={selectedIds.size < 2 || merging}
                  onClick={openMergeConfirm}
                  sx={{
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    px: 1.75,
                  }}
                >
                  {t("courierControl.mergeAction")}
                </Button>
              </Stack>
            ) : null}
          </Box>

          <Box
            sx={{
              mx: { xs: 1.25, md: 1.5 },
              mb: { xs: 1.25, md: 1.5 },
              px: { xs: 1, md: 1.25 },
              py: 1,
              borderRadius: 2.5,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              bgcolor:
                theme.palette.mode === "dark"
                  ? alpha(theme.palette.common.white, 0.04)
                  : alpha(theme.palette.common.white, 0.72),
              border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
              boxShadow: `inset 0 1px 0 ${alpha("#fff", theme.palette.mode === "dark" ? 0.04 : 0.9)}`,
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={estado}
              onChange={(_, value) => {
                if (value == null) return;
                setEstado(value);
              }}
              sx={filterToggleSx(theme)}
            >
              <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
              <ToggleButton value="PEDIDO">
                {t("courierControl.statusPedido")}
              </ToggleButton>
              <ToggleButton value="EN_CLIENTE">
                {t("courierControl.statusEnCliente")}
              </ToggleButton>
              <ToggleButton value="EN_CURSO">
                {t("courierControl.statusEnCurso")}
              </ToggleButton>
              <ToggleButton value="ENTREGADO">
                {t("courierControl.statusEntregado")}
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={direccion}
              onChange={(_, value) => setDireccion(value || "")}
              sx={filterToggleSx(theme)}
            >
              <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
              <ToggleButton value="SALIDA">
                {t("courierControl.exit")}
              </ToggleButton>
              <ToggleButton value="ENTRADA">
                {t("courierControl.entry")}
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ambito}
              onChange={(_, value) => setAmbito(value || "")}
              sx={filterToggleSx(theme)}
            >
              <ToggleButton value="">{t("courierControl.all")}</ToggleButton>
              <ToggleButton value="NACIONAL">
                {t("courierControl.national")}
              </ToggleButton>
              <ToggleButton value="INTERNACIONAL">
                {t("courierControl.international")}
              </ToggleButton>
            </ToggleButtonGroup>
            <TextField
              size="small"
              placeholder={t("courierControl.search")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{
                ...pageFieldSx(theme),
                minWidth: { xs: "100%", sm: 200 },
                flex: { sm: "1 1 220px" },
                maxWidth: { md: 360 },
                ml: { md: "auto" },
                "& .MuiInputBase-root": {
                  ...pageFieldSx(theme)["& .MuiInputBase-root"],
                  borderRadius: 999,
                  height: 34,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.background.paper, 0.85)
                      : "#fff",
                },
                "& .MuiInputBase-input": {
                  py: 0.5,
                  fontSize: "0.82rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>

        {error ? (
          <Alert severity="error" sx={{ mt: 1.5, mb: 0, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}
        {mergeInfo ? (
          <Alert
            severity="success"
            sx={{ mt: 1.5, mb: 0, borderRadius: 2 }}
            onClose={() => setMergeInfo("")}
          >
            {mergeInfo}
          </Alert>
        ) : null}
      </Box>

      <Paper
        elevation={0}
        sx={{
          ...pageContentCardSx(theme),
          p: 0,
          mt: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: "background.paper",
          backgroundImage: "none",
          background: theme.palette.background.paper,
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "auto",
            maxWidth: "100%",
            bgcolor: "background.paper",
            "& .MuiTableCell-stickyHeader": {
              bgcolor: `${theme.palette.background.paper} !important`,
              backgroundColor: `${theme.palette.background.paper} !important`,
              backgroundImage: "none !important",
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: "background.paper",
                  "& th": {
                    bgcolor: `${theme.palette.background.paper} !important`,
                    backgroundImage: "none !important",
                  },
                }}
              >
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.dateOrder")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.direction")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.origin")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.destination")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.packages")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.boxMeasures")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.grossWeight")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.createdBy")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.content")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.pieces")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volume")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.volWeight")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.chargeable")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.cost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.realCost")}
                </TableCell>
                <TableCell align="right" sx={tableHeadCellSx(theme)}>
                  {t("courierControl.difference")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.courierCompany")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.awb")}
                </TableCell>
                <TableCell sx={tableHeadCellSx(theme)}>
                  {t("courierControl.status")}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ ...tableHeadCellSx(theme), width: 88, px: 0.5 }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: 0,
                    }}
                  >
                    <Checkbox
                      size="small"
                      indeterminate={
                        selectedIds.size > 0 && !allMergeableSelected
                      }
                      checked={allMergeableSelected}
                      onChange={toggleSelectAllMergeable}
                      inputProps={{
                        "aria-label": t("courierControl.mergeAction"),
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center" sx={{ py: 6 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <LocalShippingOutlinedIcon
                        sx={{ fontSize: 36, opacity: 0.45 }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {t("courierControl.empty")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const id = row.IdCourierControl;
                  const open = expandedIds.has(id);
                  const lineas = Array.isArray(row.contenidoLineas)
                    ? row.contenidoLineas
                    : [];
                  const hasLines = lineas.length > 0 || Number(row.piezas) > 0;
                  const diff = Number(row.Diferencia);
                  const diffColor =
                    Number.isFinite(diff) && diff !== 0
                      ? diff > 0
                        ? theme.palette.error.main
                        : theme.palette.success.main
                      : "inherit";
                  const selected = selectedIds.has(id);
                  const canMergeSelect = canSelectCourierForMerge(row);
                  const isLeaveClient = isCourierLeftWithClientEnvio(row);
                  const piezasKind = classifyCourierPiezasKind(lineas);
                  const piezasCount =
                    Number(row.piezas) > 0
                      ? Number(row.piezas)
                      : piezasKind.count;
                  const contentText =
                    row.contenido ||
                    (Array.isArray(row.contenidoLabels) &&
                    row.contenidoLabels.length
                      ? row.contenidoLabels.join(", ")
                      : "") ||
                    lineas
                      .map(
                        (linea) =>
                          linea.label ||
                          linea.Estilo ||
                          linea.CodigoMuestra ||
                          "",
                      )
                      .filter(Boolean)
                      .join(", ") ||
                    "";
                  return (
                    <React.Fragment key={id}>
                      <TableRow
                        hover
                        selected={selected}
                        sx={{
                          cursor: "pointer",
                          transition: "background-color 0.15s ease",
                          bgcolor: selected
                            ? alpha(
                                theme.palette.secondary?.main || "#7b1fa2",
                                0.08,
                              )
                            : undefined,
                          "&:hover": {
                            bgcolor: selected
                              ? alpha(
                                  theme.palette.secondary?.main || "#7b1fa2",
                                  0.12,
                                )
                              : alpha(theme.palette.primary.main, 0.05),
                          },
                          "& td": {
                            borderBottom: open
                              ? "none"
                              : `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                            py: 1.15,
                          },
                        }}
                        onClick={() => openRow(id)}
                      >
                        <TableCell
                          sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
                        >
                          <Stack spacing={0.15}>
                            <span>{fmtDate(row.Fecha)}</span>
                            {(row.FechaEnvio || row.FechaEntregaDestino) && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", lineHeight: 1.2 }}
                              >
                                {row.FechaEnvio
                                  ? `${t("courierControl.dateShipShort")}: ${fmtDate(row.FechaEnvio)}`
                                  : null}
                                {row.FechaEnvio && row.FechaEntregaDestino
                                  ? " · "
                                  : null}
                                {row.FechaEntregaDestino
                                  ? `${t("courierControl.dateDeliveryShort")}: ${fmtDate(row.FechaEntregaDestino)}`
                                  : null}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.75} flexWrap="wrap">
                            <Chip
                              size="small"
                              label={directionLabel(row.Direccion)}
                              sx={directionChipSx(theme, row.Direccion)}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={scopeLabel(row.Ambito)}
                              sx={{
                                ...pageKpiChipSx,
                                height: 26,
                                fontWeight: 600,
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 160 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={row.Origen || ""}
                          >
                            {row.Origen || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            noWrap
                            title={
                              [row.Destino, row.DireccionEnvio]
                                .filter(Boolean)
                                .join(" · ") || ""
                            }
                          >
                            {row.Destino || "—"}
                            {row.DireccionEnvio ? (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                noWrap
                              >
                                {row.DireccionEnvio}
                              </Typography>
                            ) : null}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {row.bultos != null ? row.bultos : "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {fmtBoxMeasures(row)}
                          </Typography>
                          {row.TipoEmbalaje ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {row.TipoEmbalaje}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          {row.PesoBrutoKg != null && row.PesoBrutoKg !== ""
                            ? `${fmtNum(row.PesoBrutoKg, 3)} kg`
                            : "—"}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 120 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={row.CreatedByName || row.SolicitadoPor || ""}
                          >
                            {row.CreatedByName || row.SolicitadoPor || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 220, minWidth: 140 }}>
                          <Typography
                            variant="body2"
                            noWrap
                            title={contentText || ""}
                            sx={{ fontWeight: 600 }}
                          >
                            {contentText || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={piezasCount}
                            title={
                              piezasKind.kind === "swatches"
                                ? t("courierControl.piecesKindSwatches")
                                : piezasKind.kind === "mixed"
                                  ? t("courierControl.piecesKindMixed")
                                  : t("courierControl.piecesKindSamples")
                            }
                            sx={piezasKindChipSx(theme, piezasKind.kind)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.VolumenM3, 4)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.PesoVolumetricoKg, 3)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {fmtNum(row.PesoFacturableKg, 3)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.Coste, 2)}
                        </TableCell>
                        <TableCell align="right">
                          {fmtNum(row.CosteReal, 2)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, color: diffColor }}
                        >
                          {fmtNum(row.Diferencia, 2)}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 140 }}>
                          <Typography variant="body2" noWrap fontWeight={600}>
                            {row.CompaniaCourier || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {row.NumeroAwb ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "ui-monospace, monospace",
                                fontWeight: 700,
                                fontSize: "0.8rem",
                              }}
                            >
                              {row.NumeroAwb}
                            </Typography>
                          ) : isLeaveClient ? (
                            "—"
                          ) : row.Estado === "PEDIDO" ? (
                            <Chip
                              size="small"
                              label={t("courierControl.pendingBox")}
                              sx={statusChipSx(theme, "PEDIDO")}
                            />
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" gap={0.75} flexWrap="wrap">
                            {isLeaveClient ? (
                              <Chip
                                size="small"
                                label={t("courierControl.leftWithClientBadge")}
                                sx={{
                                  ...pageKpiChipSx,
                                  height: 26,
                                  fontWeight: 800,
                                  bgcolor: alpha("#6a1b9a", 0.12),
                                  color: "#6a1b9a",
                                  border: `1px solid ${alpha("#6a1b9a", 0.4)}`,
                                }}
                              />
                            ) : (
                              <Chip
                                size="small"
                                label={statusLabel(row.Estado)}
                                sx={statusChipSx(theme, row.Estado)}
                              />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ pl: 0, pr: 0.5, width: 88 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-end",
                              gap: 0,
                            }}
                          >
                            <Checkbox
                              size="small"
                              disabled={!canMergeSelect}
                              checked={selected}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                toggleSelected(id, e.target.checked)
                              }
                            />
                            <IconButton
                              size="small"
                              aria-label={
                                open
                                  ? t("courierControl.collapseContent")
                                  : t("courierControl.expandContent")
                              }
                              disabled={!hasLines}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(id);
                              }}
                            >
                              {open ? (
                                <ExpandLessIcon fontSize="small" />
                              ) : (
                                <ExpandMoreIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={20}
                          sx={{
                            py: 0,
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.45)}`,
                          }}
                        >
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                px: 2,
                                py: 1.25,
                                mr: 1,
                                mb: 1,
                                borderRadius: 2,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                                border: `1px solid ${alpha(theme.palette.divider, 0.55)}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.4,
                                  color: "text.secondary",
                                  display: "block",
                                  mb: 0.75,
                                }}
                              >
                                {t("courierControl.contentLines", {
                                  count: lineas.length || row.piezas || 0,
                                })}
                              </Typography>
                              {lineas.length ? (
                                <Box
                                  component="ul"
                                  sx={{
                                    m: 0,
                                    p: 0,
                                    listStyle: "none",
                                  }}
                                >
                                  {lineas.map((linea, idx) => {
                                    const detailParts =
                                      buildContenidoDetailParts(
                                        linea,
                                        t,
                                        fmtNum,
                                      );
                                    let motivo = linea.Motivo;
                                    if (
                                      isCourierLeftWithClientMotivo(
                                        row.Notas,
                                      ) &&
                                      normalizeCourierMotivoValue(motivo) ===
                                        "sample"
                                    ) {
                                      motivo = "leftWithClient";
                                    }
                                    const motivoKey =
                                      courierMotivoI18nKey(motivo);
                                    const motivoLabel = motivoKey
                                      ? t(motivoKey)
                                      : motivo || null;
                                    const sampExtra =
                                      linea.Estilo &&
                                      linea.CodigoMuestra &&
                                      linea.label !== linea.CodigoMuestra
                                        ? linea.CodigoMuestra
                                        : null;

                                    return (
                                      <Box
                                        component="li"
                                        key={`${id}-${idx}-${linea.label}`}
                                        sx={{
                                          display: "flex",
                                          alignItems: "flex-start",
                                          justifyContent: "space-between",
                                          gap: 1.5,
                                          py: 0.85,
                                          borderBottom:
                                            idx < lineas.length - 1
                                              ? `1px solid ${alpha(theme.palette.divider, 0.4)}`
                                              : "none",
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            minWidth: 0,
                                            flex: "1 1 220px",
                                          }}
                                        >
                                          <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 800 }}
                                          >
                                            {linea.label}
                                          </Typography>
                                          {detailParts.length ? (
                                            <Stack
                                              direction="row"
                                              spacing={0.75}
                                              flexWrap="wrap"
                                              useFlexGap
                                              sx={{ mt: 0.65 }}
                                            >
                                              {detailParts.map((p) => (
                                                <Chip
                                                  key={`${p.key}-${p.value}`}
                                                  size="small"
                                                  variant="filled"
                                                  label={`${p.label}: ${p.value}`}
                                                  sx={contenidoDetailChipSx(
                                                    theme,
                                                    p.key,
                                                  )}
                                                />
                                              ))}
                                            </Stack>
                                          ) : null}
                                        </Box>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            flexShrink: 0,
                                            textAlign: "right",
                                            maxWidth: 220,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {[sampExtra, motivoLabel]
                                            .filter(Boolean)
                                            .join(" · ") || "—"}
                                        </Typography>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {row.contenido || "—"}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CourierControlDialog
        open={dialogOpen}
        envio={draft}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onChange={setDraft}
        onSave={save}
        onDelete={remove}
        canSolicitar={allowSolicitar}
        canTerminar={allowTerminar}
        currentUser={permUser}
      />

      <Dialog
        open={mergeOpen}
        onClose={guardLockedDialogClose(() => !merging && setMergeOpen(false))}
        maxWidth="sm"
        fullWidth
        slotProps={lockedDialogSlotProps}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {t("courierControl.mergeConfirmTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.5 }}>
            {t("courierControl.mergeConfirmLead", {
              count: selectedRows.length,
            })}
          </Typography>
          <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25 }}>
            {t("courierControl.mergeConfirmKeep", {
              id: mergeValidation.targetId,
              awb: mergeValidation.awb
                ? t("courierControl.mergeConfirmAwb", {
                    awb: mergeValidation.awb,
                  })
                : "",
            })}
          </Typography>
          <Stack gap={0.75}>
            {selectedRows.map((row) => (
              <Box
                key={row.IdCourierControl}
                sx={{
                  px: 1.25,
                  py: 0.85,
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  #{row.IdCourierControl}
                  {row.NumeroAwb ? ` · ${row.NumeroAwb}` : ""}
                  {row.CompaniaCourier ? ` · ${row.CompaniaCourier}` : ""}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[row.Destino, row.contenido, statusLabel(row.Estado)]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMergeOpen(false)}
            disabled={merging}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("courierControl.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={
              merging ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <CallMergeIcon />
              )
            }
            disabled={merging || !mergeValidation.ok}
            onClick={confirmMerge}
            sx={{ textTransform: "none", fontWeight: 800, borderRadius: 999 }}
          >
            {t("courierControl.mergeConfirmAction")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
